import * as React from 'react';
import { useDispatch } from 'react-redux';

import { fetchPreviewOffer } from '../../../../../common/actions/fetchPreviewOffer/fetchPreviewOffer';
import { IDailyRentOfferForMobileSite } from '../../../../../common/repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';
import { TThunkDispatch } from '../../../../../common/types/redux';
import { EFavoriteAction, useFavoritesSubscriber } from '../../../../../common/utils/favorites';

interface IUseLocalPreviewOfferParams {
  cianId: number | undefined;
  isListingOffersLoading: boolean;
  listingOffer: IDailyRentOfferForMobileSite | undefined;
}

interface IUseLocalPreviewOfferResult {
  isPreviewOfferLoading: boolean;
  offer: IDailyRentOfferForMobileSite | undefined;
  retryPreviewOffer(): void;
}

function getPreviewOffer(offer: IDailyRentOfferForMobileSite | undefined, cianId: number | undefined) {
  if (!offer || !cianId) {
    return undefined;
  }

  return offer.id === cianId || offer.cianId === cianId ? offer : undefined;
}

// Нужно при работе с закрытым листингом, в таком случае оффера нет в листинге и его надо загрузить специально для превью
export function useLocalPreviewOffer({
  cianId,
  isListingOffersLoading,
  listingOffer,
}: IUseLocalPreviewOfferParams): IUseLocalPreviewOfferResult {
  const reduxDispatch = useDispatch<TThunkDispatch>();
  const requestedPreviewOfferIdRef = React.useRef<number | undefined>();
  const cachedListingOfferIdRef = React.useRef<number | undefined>();
  const [localPreviewOffer, setLocalPreviewOffer] = React.useState<IDailyRentOfferForMobileSite | undefined>();
  const [isLocalPreviewOfferLoading, setIsLocalPreviewOfferLoading] = React.useState(false);
  const lastFavoritesAction = useFavoritesSubscriber();

  // Listing имеет приоритет: локальный offer нужен только как fallback для preview.
  const localPreviewOfferForCurrentPin = getPreviewOffer(localPreviewOffer, cianId);
  const offer = listingOffer || localPreviewOfferForCurrentPin;

  // Кэшируем listingOffer один раз на пин: при закрытии списка он пропадёт из listingOffer, и без
  // кэша превью моргнёт (offer → undefined → loading → догрузка). Пишем однократно (guard по cianId) —
  // пока список открыт, показывается сам listingOffer (приоритетнее кэша), поэтому держать кэш свежим
  // не нужно; избранное для закрытого списка досинхронизирует favorites-эффект ниже.
  React.useEffect(() => {
    if (listingOffer && cachedListingOfferIdRef.current !== cianId) {
      cachedListingOfferIdRef.current = cianId;
      setLocalPreviewOffer(listingOffer);
    }
  }, [cianId, listingOffer]);

  const shouldFetchLocalPreviewOffer = Boolean(
    cianId && requestedPreviewOfferIdRef.current !== cianId && !offer && !isListingOffersLoading,
  );
  const isPreviewOfferLoading = isListingOffersLoading || shouldFetchLocalPreviewOffer || isLocalPreviewOfferLoading;

  const loadPreviewOffer = React.useCallback(
    (offerId: number) => {
      requestedPreviewOfferIdRef.current = offerId;
      setIsLocalPreviewOfferLoading(true);

      Promise.resolve(reduxDispatch(fetchPreviewOffer(offerId)))
        .then(setLocalPreviewOffer)
        .catch(() => {
          setLocalPreviewOffer(undefined);
        })
        .finally(() => {
          setIsLocalPreviewOfferLoading(false);
        });
    },
    [reduxDispatch],
  );

  React.useEffect(() => {
    if (!shouldFetchLocalPreviewOffer || !cianId) {
      return;
    }

    loadPreviewOffer(cianId);
  }, [cianId, loadPreviewOffer, shouldFetchLocalPreviewOffer]);

  // Локальный preview-оффер не в redux, поэтому правки избранного к нему применяем через подписку на favorites-экшены
  React.useEffect(() => {
    if (!lastFavoritesAction) {
      return;
    }

    setLocalPreviewOffer(prev =>
      prev && prev.id === lastFavoritesAction.offerId
        ? { ...prev, isFavorite: lastFavoritesAction.action === EFavoriteAction.Added }
        : prev,
    );
  }, [lastFavoritesAction]);

  const retryPreviewOffer = React.useCallback(() => {
    if (!cianId) {
      return;
    }

    loadPreviewOffer(cianId);
  }, [cianId, loadPreviewOffer]);

  return {
    isPreviewOfferLoading,
    offer,
    retryPreviewOffer,
  };
}
